import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button as MuiButton, Paper, Typography } from '@mui/material';
import { Copy } from 'lucide-react';
import { toast } from '../../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { Button } from '../../../../../../../ui';
import { JobPostingTabData } from '../../../../../../../utils/types/JobOpenings';

interface UnifiedActionsProps {
  resolvedPostId?: string;
  isSaving: boolean;
  jobPostingData: JobPostingTabData;
  onSaveOrCreate: () => void;
}

export const UnifiedActions: React.FC<UnifiedActionsProps> = ({
  resolvedPostId,
  isSaving,
  jobPostingData,
  onSaveOrCreate,
}) => {
  const { t } = useTranslation(NS.adminPages);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/jobs/${resolvedPostId}`);
    toast.success(t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.urlCopied) || 'Link copied!', {
      position: 'bottom-center',
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'success.light',
        bgcolor: 'background.paper',
        p: 2,
        boxShadow: (theme) => theme.shadows[1],
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { sm: 'center' },
          justifyContent: { sm: 'space-between' },
        }}
      >
        <Box>
          <Typography variant='subtitle1' sx={{ fontWeight: 800, color: 'text.primary' }}>
            {resolvedPostId
              ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updateJobPost) || 'Update Job Post'
              : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.publishJob) || 'Publish Job Post'}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {resolvedPostId
              ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updateSubtitle) || 'Save edits to the public site'
              : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.publishSubtitle) || 'Launch your job posting online'}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {resolvedPostId && (
            <MuiButton
              onClick={handleCopyLink}
              variant='outlined'
              size='medium'
              startIcon={<Copy size={16} />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'divider',
                },
              }}
            >
              Copy Link
            </MuiButton>
          )}
          <Button
            onClick={onSaveOrCreate}
            variant='primary'
            label={
              isSaving
                ? resolvedPostId
                  ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updating) || 'Updating...'
                  : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.creating) || 'Creating...'
                : resolvedPostId
                  ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updateButton) || 'Update Post'
                  : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.createButton) || 'Create Post'
            }
            sx={{ width: { xs: '100%', sm: 'auto' }, fontWeight: 600 }}
            disabled={
              isSaving ||
              !jobPostingData.jobTitle ||
              !jobPostingData.department ||
              !jobPostingData.location
            }
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default UnifiedActions;
